import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Css/BlogsViews.css';
import Topnavbar from '../Header-pages/Topnavbar';
import Navbar from '../Header-pages/Navbar';
import Footer from '../Header-pages/Footer';
import { API_URL } from '../utils/Function';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';

export default function Blogsviews() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [animatedCards, setAnimatedCards] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const blogsContainerRef = useRef(null);
const [hoverId, setHoverId] = useState(null);

  // Fetch blogs from backend
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/blog.php/get?status=published`);
      const data = await response.json();
      
      if (data.success) {
        setBlogs(data.blogs);
        
        // Extract unique author IDs from blogs
        const authorIds = [...new Set(data.blogs.map(blog => blog.author_id))];
        if (authorIds.length > 0) {
          fetchUsers(authorIds);
        }
      } else {
        console.error('Error fetching blogs:', data.error);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/category.php/get?type=blog`);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch users data based on author IDs
  const fetchUsers = async (authorIds) => {
    try {
      const response = await fetch(`${API_URL}/user.php/get`);
      const data = await response.json();
      
      if (data.success) {
        const usersMap = {};
        data.users.forEach(user => {
          usersMap[user.id] = user;
        });
        setUsers(usersMap);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, []);

  // Function to handle blog click
  const handleBlogClick = (blogSlug) => {
    navigate(`/blog/${blogSlug}`);
  };

  // Dynamic filter links based on categories that have blogs
  const filterLinks = useMemo(() => {
    const staticLinks = [
      { id: 'all', name: 'All', icon: '/images/i.png', width: '90px' }
    ];

    // Get unique category IDs from blogs
    const usedCategoryIds = [...new Set(blogs.map(blog => blog.category_id))];
    
    // Filter categories to only include those that have blogs
    const categoriesWithBlogs = categories.filter(category => 
      usedCategoryIds.includes(category.id)
    );

    // Map categories to filter links
    const categoryLinks = categoriesWithBlogs.map(category => {
      // Map category names to icons
      const getCategoryIcon = (categoryName) => {
        const iconMap = {
          'Tech News': '/images/Frame.png',
          'Case Studies': '/images/case.png',
          'School Management': '/images/school-management-icon.png',
          'AI Solutions': '/images/ai-solutions-icon.png',
          'Security': '/images/security-icon.png',
          'Cloud Tech': '/images/cloud-icon.png',
          'Analytics': '/images/analytics-icon.png',
          'Mobile Tech': '/images/mobile-icon.png'
        };
        
        return iconMap[categoryName] || '/images/default-category-icon.png';
      };

      // Calculate width based on category name length
      const calculateWidth = (name) => {
        const baseWidth = 120;
        const charWidth = 8;
        return `${baseWidth + (name.length * charWidth)}px`;
      };

      return {
        id: category.id.toString(),
        name: category.name,
        icon: getCategoryIcon(category.name),
        width: calculateWidth(category.name),
        categoryId: category.id
      };
    });

    return [...staticLinks, ...categoryLinks];
  }, [categories, blogs]);

  // Map backend blog data to frontend format
  const blogCards = useMemo(() => {
    if (!blogs.length) return [];

    return blogs.map(blog => {
      // Get category name and color
      const category = categories.find(cat => cat.id === blog.category_id) || {};
      const categoryName = category.name || 'Uncategorized';
      
      // Map category names to colors
      const categoryColors = {
        'Tech News': '#007BFF',
        'Case Studies': '#22C55E',
        'Security': '#EF4444',
        'Cloud Tech': '#A855F7',
        'Analytics': '#F97316',
        'Mobile Tech': '#45B7D1',
        'AI Solutions': '#8B5CF6',
        'School Management': '#06B6D4'
      };

      const categoryColor = categoryColors[categoryName] || '#6B7280';

      // Format date
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      };

      // Calculate read time based on content length
      const calculateReadTime = (content) => {
        if (!content) return '2 min read';
        const wordsPerMinute = 200;
        const words = content.split(/\s+/).length;
        const minutes = Math.ceil(words / wordsPerMinute);
        return `${minutes} min read`;
      };

      // Get featured image or first gallery image
      const getBlogImage = (blog) => {
        if (blog.featured_image) {
          return `${API_URL}/uploads/${blog.featured_image}`;
        }
        
        if (blog.gallery_images) {
          try {
            const gallery = JSON.parse(blog.gallery_images);
            if (gallery.length > 0) {
              return `${API_URL}/uploads/${gallery[0]}`;
            }
          } catch (e) {
            console.error('Error parsing gallery images:', e);
          }
        }
        
        return '/images/default-blog.png';
      };

      // Get author data from users state
      const author = users[blog.author_id] || {};
      const authorName = author.name || blog.author_name || 'Unknown Author';
      const authorImage = author.image_url 
        ? author.image_url 
        : author.image 
          ? `${API_URL}/uploads/${author.image}`
          : null;

      return {
        id: blog.id,
        title: blog.title,
        excerpt: blog.excerpt || blog.content?.substring(0, 150) + '...' || 'No excerpt available',
        category: categoryName,
        categoryColor: categoryColor,
        date: formatDate(blog.published_at || blog.created_at),
        time: calculateReadTime(blog.content),
        author: {
          name: authorName,
          image: authorImage
        },
        image: getBlogImage(blog),
        author_id: blog.author_id,
        category_id: blog.category_id,
        slug: blog.slug
      };
    });
  }, [blogs, categories, users]);

  useEffect(() => {
    const container = blogsContainerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedCards(blogCards.map((card) => card.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    if (container) observer.observe(container);

    return () => {
      if (container) observer.unobserve(container);
    };
  }, [blogCards]);

  // Filter blogs based on active filter
  const filteredBlogs = useMemo(() => {
    if (activeFilter === 'all') {
      return blogCards;
    }
    
    // For category filters, use the category ID from filterLinks
    const activeFilterLink = filterLinks.find(link => link.id === activeFilter);
    if (activeFilterLink && activeFilterLink.categoryId) {
      return blogCards.filter(blog => blog.category_id === activeFilterLink.categoryId);
    }
    
    return blogCards;
  }, [blogCards, activeFilter, filterLinks]);

  return (
    <>
      <Topnavbar />
      <Navbar />
      
      {/* Hero Section */}
      <div className='blogsviews-hero-section'>
        <img 
          src='/images/Productview.jpg' 
          alt='Blogs' 
          className='blogsviews-hero-image'
        />
        <div className='blogsviews-hero-overlay'>
          {/* Breadcrumbs inside hero */}
          <div className='blogsviews-hero-breadcrumbs'>
            <a onClick={()=>navigate('/')} style={{color:'#fff', cursor:'pointer'}}>Home</a>
            <span className='blogsviews-breadcrumb-separator'>/</span>
            <span className='blogsviews-breadcrumb-current' style={{color:'#fff', cursor:'pointer'}}>Blogs</span>
          </div>
          
          <div className='blogsviews-hero-content'>
            <h1>All Blogs</h1>
            <p>Explore all our latest insights, technology trends, and expert updates from SafPro.</p>
          </div>
        </div>
      </div>

      {/* Main Blogs Content */}
      <div className="blogsviews-container" ref={blogsContainerRef}>
        <div className="blogsviews-content">
          {/* Filter Section */}
          <div className="blogsviews-filter-section">
            <div className="filter-links" style={{
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginTop: "20px",
    flexWrap: "nowrap",
    overflowX: "auto",
    padding: "10px 0",
    width: "100%",
    maxWidth: "1000px"
}}>
    {filterLinks.map((link) => {

        const isActive = activeFilter === link.id;
        const isHover = hoverId === link.id;

        return (
            <button
                key={link.id}
                onMouseEnter={() => setHoverId(link.id)}
                onMouseLeave={() => setHoverId(null)}
                onClick={() => setActiveFilter(link.id)}
                style={{
                    height: "50px",
                    borderRadius: "9999px",
                    border: "1px solid",
                    borderColor: isActive || isHover ? "#4d3dd6" : "#E5E7EB",
                    background: isActive || isHover ? "#4d3dd6" : "#FFFFFF",
                    color: isActive || isHover ? "#FFFFFF" : "#374151",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    padding: "0 20px",
                    minWidth: "fit-content",
                    width: link.width
                }}
            >
                <span style={{ color: isActive || isHover ? "#fff" : "#374151" }}>
                    {link.name}
                </span>
            </button>
        );
    })}
</div>

          </div>

          {/* Blog Cards Grid */}
          <div className="blog-cards-grid blogsviews-grid">
            {loading ? (
              Array.from({ length: 9 }).map((_, index) => (
                <div key={index} className="blog-card loading">
                  <div className="card-image loading-skeleton"></div>
                  <div className="card-content">
                    <div className="loading-skeleton title-skeleton"></div>
                    <div className="loading-skeleton excerpt-skeleton"></div>
                    <div className="card-footer">
                      <div className="author-info">
                        <div className="author-image loading-skeleton"></div>
                        <div className="author-details">
                          <div className="loading-skeleton name-skeleton"></div>
                          <div className="loading-skeleton date-skeleton"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : filteredBlogs.length > 0 ? (
              filteredBlogs.map((card) => (
                <div
                  key={card.id}
                  className={`blog-card ${animatedCards.includes(card.id) ? 'animate' : ''}`}
                >
                  <div className="card-images">
                    <img src={card.image} alt={card.title} />
                    <div
                      className="image-category-badge"
                      style={{ backgroundColor: card.categoryColor }}
                    >
                      {card.category}
                    </div>
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">{card.title}</h3>
                    <p className="card-excerpt">{card.excerpt}</p>
                    <div className="card-footer">
                      <div className="author-info">
                        {card.author.image ? (
                          <img
                            src={`${API_URL}/${card.author.image}`}
                            alt={card.author.name}
                            className="author-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : (
                          <div className="author-icon">
                            <FontAwesomeIcon icon={faUser} />
                          </div>
                        )}
                        {!card.author.image && (
                          <div className="author-icon" style={{display: 'flex'}}>
                            <FontAwesomeIcon icon={faUser} />
                          </div>
                        )}
                        <div className="author-details">
                          <span className="author-name">{card.author.name}</span>
                          <span className="post-date">
                            {card.date} • {card.time}
                          </span>
                        </div>
                      </div>
                      <button 
                        className="read-more-btn"
                        onClick={() => handleBlogClick(card.slug)}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.16675 10H15.8334"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.8334 5L15.8334 10L10.8334 15"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-blogs-message">
                <p>No blogs found for this category. Check back later for new insights!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}